<template>
  <div class="direct-message-queue-table">
    <div class="mb-x1">
      <el-button type="primary" @click="onClickMessageQueueCreate"
        ><i class="mdi mdi-plus"></i>メッセージの新規作成</el-button
      >
    </div>
    <el-table
      :data="messageQueues"
      stripe
      @header-click="headerClick"
      empty-text="データがありません。"
      style="width: 100%"
    >
      <el-table-column label="ステータス" sort-by="-status" :render-header="renderHeader" min-width="25%">
        <template slot-scope="scope">
          <div class="text-info" v-if="scope.row.status === 'scheduled'">スケジュール済み</div>
          <div class="text-primary" v-if="scope.row.status === 'sending'">
            <template v-if="scope.row.isSendStop">配信中(停止中)</template>
            <template v-else>配信中</template>
          </div>
          <div class="text-primary" v-if="scope.row.status === 'paused'">
            <template v-if="scope.row.isSendStop">停止中</template>
            <template v-else>一時停止</template>
          </div>
          <div class="text-success" v-if="scope.row.status === 'sent'">
            <template v-if="scope.row.isSendStop">配信済み(配信停止)</template>
            <template v-else>配信済み</template>
          </div>
          <div class="text-disabled" v-if="scope.row.status === 'draft'">下書き</div>
        </template>
      </el-table-column>
      <el-table-column label="種別" sort-by="type" :render-header="renderHeader" min-width="25%">
        <template slot-scope="scope">
          <div v-if="scope.row.type === 0">当選通知</div>
          <div v-if="scope.row.type === 2">リマインド</div>
          <div v-if="scope.row.type === 3">シリアルコード未使用のリマインド</div>
        </template>
      </el-table-column>
      <el-table-column label="配信方法" min-width="20%">
        <template slot-scope="scope">
          <div v-if="scope.row.sendType === 'DM'">DM</div>
          <div v-if="scope.row.sendType === 'mention'">メンション</div>
        </template>
      </el-table-column>
      <el-table-column label="配信開始日時" sort-by="-actionDatetime" :render-header="renderHeader" min-width="25%">
        <template slot-scope="scope">
          <div>{{ formatDate(scope.row.actionDatetime) }}</div>
          <div>{{ formatTime(scope.row.actionDatetime) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="配信対象数" min-width="15%">
        <template slot-scope="scope">{{ scope.row.scheduleCount }}</template>
      </el-table-column>
      <el-table-column label="配信結果" min-width="15%">
        <template slot-scope="scope">
          <div v-if="isUnsent(scope.row.status)">-</div>
          <div v-else>
            <div>{{ scope.row.sentCount }}</div>
            <div>{{ '失敗' }}: {{ scope.row.failedCount }}</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column min-width="35%">
        <template slot-scope="scope">
          <div layout layout-align="start">
            <el-tooltip placement="top" content="配信対象">
              <el-button
                class="el-button--icon"
                v-show="scope.row.status === 'scheduled' || scope.row.status === 'draft'"
                type="default"
                @click="getMessageQueuesId(scope.row.id, scope.row.status)"
                ><i class="mdi mdi-playlist-play"></i
              ></el-button>
            </el-tooltip>
            <el-tooltip placement="top" content="配信履歴">
              <el-button
                class="el-button--icon"
                v-if="scope.row.status === 'sending' || scope.row.status === 'sent' || scope.row.status === 'paused'"
                @click="onClickMessageQueueDetail(scope.row.id)"
                type="default"
                ><i class="mdi mdi-playlist-play"></i
              ></el-button>
            </el-tooltip>
            <el-tooltip placement="top" content="編集">
              <el-button class="el-button--icon" type="default" @click="updateMessageQueue(scope.row.id, '編集')"
                ><i class="mdi mdi-table-edit"></i
              ></el-button>
            </el-tooltip>
            <el-tooltip placement="top" content="複製">
              <el-button class="el-button--icon" type="default" @click="updateMessageQueue(scope.row.id, '複製')"
                ><i class="mdi mdi-content-duplicate"></i
              ></el-button>
            </el-tooltip>
            <el-tooltip placement="top" content="削除">
              <el-button
                class="el-button--icon"
                v-show="scope.row.status === 'scheduled' || scope.row.status === 'draft'"
                type="default"
                @click="deleteMessageQueue(scope.row.id)"
                ><i class="mdi mdi-delete"></i
              ></el-button>
            </el-tooltip>
            <el-tooltip placement="top" content="配信停止">
              <el-button
                class="el-button--icon"
                v-show="scope.row.status === 'sending' || scope.row.status === 'paused'"
                :disabled="scope.row.isSendStop === 1"
                type="default"
                @click="stopSend(scope.row.id)"
                ><i class="mdi mdi-cancel"></i
              ></el-button>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.direct-message-queue-table {
  .el-button + .el-button {
    margin-left: $spacing-unit;
  }

  .el-button {
    margin-left: $spacing-unit;
  }
}
</style>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapState } from 'pinia'
import { defineComponent, PropType } from 'vue'

import api from '@/client/core/api'
import Util from '@/client/core/util'
import { formatDate, formatTime } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import { useTwitterDirectMessageDialogStore } from '@/client/store/useTwitterDirectMessageDialogStore'

export default defineComponent({
  props: {
    messageQueues: Array as PropType<readonly unknown[]>,
    options: Object,
    onChangeSortBy: Function,
    onChange: Function,
    onClickMessageQueueEdit: Function,
    onClickGetMessageQueuesId: Function,
    onClickMessageQueueCreate: Function,
    onClickMessageQueueDetail: {
      type: Function,
      default: () => {
        return
      }
    }
  },
  computed: {
    ...mapState(useTwitterDirectMessageDialogStore, {
      campaignId: state => state.campaignId,
      prizeId: state => state.prizeId
    })
  },
  methods: {
    formatDate,
    formatTime,

    /**
     * メッセージキューを更新
     * @param {number} messageQueueId メッセージキューId
     * @param {string} status
     */
    getMessageQueuesId(messageQueueId: number, status: string) {
      TrackingService.sendEvent('click:当選メッセージの管理|メッセージ管理|配信対象')

      this.onClickGetMessageQueuesId(messageQueueId, status)
    },

    /**
     * 配信対象を取得する
     * @param {number} messageQueueId メッセージキューID
     */
    updateMessageQueue(messageQueueId: number, action: string) {
      TrackingService.sendEvent(`click:当選メッセージの管理|メッセージ管理|${action}`)

      const isCopy = action === '複製'

      this.onClickMessageQueueEdit(messageQueueId, isCopy)
    },

    /**
     * メッセージキューを削除
     * @param {number} messageQueueId メッセージキューID
     */
    async deleteMessageQueue(messageQueueId: number) {
      TrackingService.sendEvent('click:当選メッセージの管理|メッセージ管理|削除')

      const confirm = window.confirm('メッセージ配信スケジュールを削除してもよろしいですか?')
      if (!confirm) {
        return
      }
      const result = await api.delete('/twitter_message_queues/' + messageQueueId)

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象のメッセージ配信スケジュールはすでに削除されています。',
          customClass: 'danger',
          duration: 5000
        })
        if (typeof this.onChange === 'function') {
          this.onChange()
        }
        return
      }

      // エラーがある場合はエラーメッセージを表示
      if (!result.data) {
        this.$notify({
          title: 'メッセージ配信スケジュールの削除に失敗しました。',
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'メッセージ配信スケジュールを削除しました。',
        customClass: 'success',
        duration: 5000
      })

      if (typeof this.onChange === 'function') {
        this.onChange()
      }
    },

    /**
     * メッセージ配信停止
     * @param {number} id
     * @returns {void}
     */
    async stopSend(id: number) {
      let message = `${'メッセージの配信を停止してもよろしいですか？'}\n`
      message += '配信停止の取り消しは行なえません。'

      const confirm = window.confirm(message)

      if (!confirm) {
        return
      }

      const params = {
        campaignId: this.campaignId,
        twCampaignPrizeId: this.prizeId,
        isSendStop: 1
      }
      const result = await api.put(`/twitter_message_queues/${id}`, params)

      // エラーがある場合はエラーメッセージを表示
      if (!result.data) {
        this.$notify({
          title: '配信停止に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'メッセージを配信停止しました。',
        message: '配信停止まで、5分ほどかかる場合がございます。',
        customClass: 'success',
        duration: 5000
      })

      if (typeof this.onChange === 'function') {
        this.onChange()
      }
    },

    /**
     * ヘッダーをクリックするとソートする
     * @param {*} column
     */
    headerClick(column: any) {
      switch (column.label) {
        case 'ステータス':
          TrackingService.sendEvent('sort:当選メッセージの管理|メッセージ管理|ステータス')
          break
        case '種別':
          TrackingService.sendEvent('sort:当選メッセージの管理|メッセージ管理|種別')
          break
        case '配信方法':
          TrackingService.sendEvent('sort:当選メッセージの管理|メッセージ管理|配信方法')
          break
        case '配信開始日時':
          TrackingService.sendEvent('sort:当選メッセージの管理|メッセージ管理|配信開始日時')
          break
        default:
          break
      }

      if (!column.sortBy) {
        return
      }

      if (column.sortBy.indexOf('-') === 0) {
        if (this.options.sortBy === column.sortBy) {
          this.onChangeSortBy(column.sortBy.substring(1))
        } else {
          this.onChangeSortBy(column.sortBy)
        }
      } else {
        if (this.options.sortBy === column.sortBy) {
          this.onChangeSortBy('-' + column.sortBy)
        } else {
          this.onChangeSortBy(column.sortBy)
        }
      }
    },
    /**
     * ヘッダー描画
     * @param {*} createElement
     * @param {*} { column }
     */
    renderHeader(createElement: any, { column }: any) {
      return Util.renderHeader(createElement, column.label, Util.getSortClass(this.options.sortBy, column.sortBy))
    },
    /**
     * ステータスをチェック
     * @param {string} status
     */
    isUnsent(status: string) {
      return status === 'scheduled' || status === 'draft'
    }
  }
})
</script>
